import { createI18n } from 'vue-i18n'

/**
 * Three languages: French (default, the source of every text), Arabic
 * (right to left) and English. French is also the fallback: a text missing in
 * Arabic shows in French, the language the platform is written in. Arabic and
 * English are loaded on first use.
 *
 * Messages live in src/locales/<locale>/<namespace>.json; the namespace is
 * the file name (common.json → t('common.save')).
 */
export const LOCALES = ['fr', 'ar', 'en'] as const
export type Locale = (typeof LOCALES)[number]
export const isLocale = (v: unknown): v is Locale => LOCALES.includes(v as Locale)
export const isRtl = (l: Locale): boolean => l === 'ar'

type Messages = Record<string, unknown>

const eager = import.meta.glob<{ default: Messages }>('../locales/fr/*.json', { eager: true })
const lazy = import.meta.glob<{ default: Messages }>('../locales/{ar,en}/*.json')

const namespace = (path: string): string => /\/([^/]+)\.json$/.exec(path)![1]!

function bundle(files: Record<string, { default: Messages }>): Messages {
  return Object.fromEntries(Object.entries(files).map(([p, m]) => [namespace(p), m.default]))
}

/**
 * Plural index from the CLDR category. Arabic has six forms
 * ("zero | one | two | few | many | other"); French counts 0 as singular.
 */
function pluralIndex(locale: string) {
  const rules = new Intl.PluralRules(locale)
  const order = ['zero', 'one', 'two', 'few', 'many', 'other']
  return (n: number, forms: number): number => {
    if (forms === 6) return order.indexOf(rules.select(Math.abs(n)))
    if (forms === 3) return n === 0 ? 0 : rules.select(Math.abs(n)) === 'one' ? 1 : 2
    return rules.select(Math.abs(n)) === 'one' ? 0 : 1
  }
}

export const i18n = createI18n({
  legacy: false,
  locale: 'fr',
  fallbackLocale: 'fr',
  messages: { fr: bundle(eager) } as never,
  pluralRules: { fr: pluralIndex('fr'), ar: pluralIndex('ar'), en: pluralIndex('en') },
  missingWarn: import.meta.env.DEV,
  fallbackWarn: false,
})

const loaded = new Set<Locale>(['fr'])

async function load(l: Locale): Promise<void> {
  if (loaded.has(l)) return
  const files = Object.fromEntries(await Promise.all(Object.entries(lazy)
    .filter(([p]) => p.includes(`/locales/${l}/`))
    .map(async ([p, load]) => [p, await load()] as const)))
  i18n.global.setLocaleMessage(l, bundle(files) as never)
  loaded.add(l)
}

const KEY = 'tsh-locale'

/** The language chosen on this workstation, if any. */
export function storedLocale(): Locale | null {
  try {
    const v = localStorage.getItem(KEY)
    return isLocale(v) ? v : null
  } catch {
    return null
  }
}

/** Last call wins: a slow chunk must not override a language picked after it. */
let request = 0

export async function setLocale(l: Locale, remember = false): Promise<void> {
  const mine = ++request
  try {
    await load(l)
  } catch {
    // The language's chunk did not load (network): keep the current language.
    return
  }
  if (mine !== request) return
  i18n.global.locale.value = l
  const root = document.documentElement
  root.lang = l
  root.dir = isRtl(l) ? 'rtl' : 'ltr'
  if (remember) {
    try { localStorage.setItem(KEY, l) } catch { /* kept for this session only */ }
  }
}

export const currentLocale = (): Locale => i18n.global.locale.value as Locale
